import React, { useState, useEffect, useRef } from "react";
import BackButton from "../../components/BackButton/BackButton";
import InfoSquare from "../../img/InfoSquare";
import Button from "../../components/Button/Button";
import Input from "../../components/Input/Input";
import SendArrow from "../../img/SendArrow";
import axios from "axios";
import "./styles.css";

function ScheduleVisit({
  onBack,
  onSchedule,
  sendInfoData,
  userData,
  deliveryMethod,
}) {
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [selectedHourIndex, setSelectedHourIndex] = useState(null);
  const [scheduleId, setScheduleId] = useState("");
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [dates, setDates] = useState([]);
  const [hours, setHours] = useState([]);
  const [noAppointments, setNoAppointments] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [firstRequestSuccess, setFirstRequestSuccess] = useState(false);
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");

  const urlEndpoint = window.urlEndpoint;

  const tagend = window.tagend;
  const inputFieldsRef = useRef(null);
  const hoursSectionRef = useRef(null); // Ref para la sección de horas

  useEffect(() => {
    const today = new Date();
    today.setDate(today.getDate() + 6);
    const futureDate = today.toISOString().split("T")[0];

    let apiUrl = "";

    if (deliveryMethod === "domicilio") {
      apiUrl = `https://api.quieromac.com/agendas/schedules/available?date=${futureDate}`;
    } else if (deliveryMethod === "punto de venta" && sendInfoData?.selectedSede?.id) {
      apiUrl = `https://api.quieromac.com/agendas/schedules/available/${sendInfoData.selectedSede.id}?date=${futureDate}`;
    }

    if (!apiUrl) return;

    axios
      .get(apiUrl)
      .then((response) => {
        const schedules = response.data.data;
        const dateSlots = {};

        let allSlotsEmpty = true; // Inicialmente asumimos que todos están vacíos

        schedules.forEach((schedule) => {
          Object.keys(schedule.available_slots).forEach((date) => {
            const slot = schedule.available_slots[date];

            // Verificamos si hay al menos un slot disponible en cada fecha
            if (slot && Object.keys(slot).length > 0) {
              allSlotsEmpty = false; // Si encontramos un slot, ya no están todos vacíos
              if (!dateSlots[date]) {
                dateSlots[date] = [];
              }
              dateSlots[date].push({
                ...slot,
                scheduleId: schedule.schedule_id,
              });
            }
          });
        });

        const availableDates = Object.keys(dateSlots).map((key) => {
          const [year, month, day] = key.split("-");
          const date = new Date(year, month - 1, day);
          const dayOfWeekFull = date.toLocaleDateString("es-ES", {
            weekday: "long",
          });
          const dayOfWeek = dayOfWeekFull.substring(0, 3).toUpperCase();
          const monthName = date.toLocaleDateString("es-ES", { month: "long" });

          let hours = dateSlots[key].flat();

          // Crear un Map para evitar duplicados de horas
          const uniqueHoursMap = new Map();
          hours.forEach((hour) => {
            const hourKey = `${hour.start}-${hour.end}`; // Combinar start y end como clave única
            if (!uniqueHoursMap.has(hourKey)) {
              uniqueHoursMap.set(hourKey, hour); // Si no existe en el mapa, agregarlo
            }
          });

          // Convertir el Map en un array de horas únicas
          const uniqueHours = Array.from(uniqueHoursMap.values());

          // Ordenar las horas
          uniqueHours.sort((a, b) => {
            if (!a.start || !b.start) return 0;

            const [aStartHour, aStartMinute] = a.start.split(":").map(Number);
            const [bStartHour, bStartMinute] = b.start.split(":").map(Number);
            return aStartHour - bStartHour || aStartMinute - bStartMinute;
          });

          return {
            key,
            dayOfWeek,
            day: parseInt(day, 10),
            month: monthName,
            hours: uniqueHours, // Ahora contiene solo horas únicas
          };
        });

        availableDates.sort((a, b) => new Date(a.key) - new Date(b.key));

        setDates(availableDates);

        // Solo mostramos el mensaje si realmente no hay slots en ninguna fecha
        if (allSlotsEmpty) {
          setNoAppointments(true);
        } else {
          setNoAppointments(false);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, [sendInfoData?.selectedSede?.id, deliveryMethod]);

  useEffect(() => {
    if (selectedDateKey !== null) {
      setShouldScroll(true);
    }
  }, [selectedDateKey]);

  useEffect(() => {
    if (
      selectedDateKey !== null &&
      selectedHourIndex !== null &&
      (deliveryMethod !== "domicilio" ||
        (deliveryMethod === "domicilio" && address.trim() !== ""))
    ) {
      setIsButtonEnabled(true);
    } else {
      setIsButtonEnabled(false);
    }
  }, [selectedDateKey, selectedHourIndex, deliveryMethod, address]);

  useEffect(() => {
    if (shouldScroll && hoursSectionRef.current) {
      hoursSectionRef.current.scrollIntoView({ behavior: "smooth" });
      setShouldScroll(false);
    }
  }, [shouldScroll]);

  const handleDateClick = (key) => {
    setSelectedDateKey(key);
    setSelectedHourIndex(null);
    const selected = dates.find((date) => date.key === key);
    setHours(selected ? selected.hours : []);
  };

  const handleHourClick = (index) => {
    setSelectedHourIndex(index);
    if (hours[index]) {
      setScheduleId(hours[index].scheduleId);
      setShouldScroll(true);
    }
  };

  const handleScheduleClick = async () => {
    if (isButtonEnabled) {
      const isHomeDelivery = deliveryMethod === "domicilio";

      // Datos comunes para ambos métodos
      const commonData = {
        customer_name: "", // Nombre del cliente
        customer_email: userData?.email, // Correo del cliente
      };

      let apiUrl = "";
      let bodyData = {};

      if (isHomeDelivery) {
        // Datos para el envío a domicilio
        apiUrl = "https://api.quieromac.com/agendas/appointments/delivery";
        bodyData = {
          ...commonData,
          address: address, // Dirección del cliente
          address2: address2 || "", // Complemento de dirección
          date: selectedDateKey,
          start_time: hours[selectedHourIndex]?.start,
          end_time: hours[selectedHourIndex]?.end,
          schedule_id: scheduleId,
        };
      } else {
        // Datos para la cita en punto de venta
        apiUrl = "https://api.quieromac.com/agendas/appointments/";
        bodyData = {
          ...commonData,
          schedule_id: scheduleId, // ID de la cita seleccionada
          date: selectedDateKey,
          start_time: hours[selectedHourIndex]?.start,
          end_time: hours[selectedHourIndex]?.end,
          point_of_sale: sendInfoData?.selectedSede?.name, // Nombre del punto de venta
        };
      }

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(bodyData),
        });

        if (response.ok) {
          console.log("Cita agendada con éxito");
          setFirstRequestSuccess(true);
          onSchedule({ ...bodyData, deliveryMethod, scheduleId });
        } else {
          console.error("Error en la solicitud al endpoint");
        }
      } catch (error) {
        console.error("Error al realizar la solicitud:", error);
      }
    }
  };

  return (
    <div className="scheduleVisit">
      <h1>Agenda el día y la hora de tu visita</h1>
      <p className="description">
        <span>Agenda una cita con nuestro asesor,</span> quien verificará el
        estado de tu dispositivo y te ayudará en el proceso.
      </p>
      <div className="important">
        <InfoSquare />
        <p>
          Recuerda que tienes 2 días para visitar nuestra tienda y hacer la
          entrega de tu dispositivo
        </p>
      </div>

      {noAppointments && (
        <p className="noAppointmentsMessage">
          No hay horarios disponibles en este momento. Puedes intentar buscar en otro punto de venta.
        </p>
      )}

      {!noAppointments && (
        <>
          <div className="dates">
            {dates.map((date) => (
              <div key={date.key} onClick={() => handleDateClick(date.key)}>
                <p className="weekDay">{date.dayOfWeek}</p>
                <div
                  className={`date ${
                    selectedDateKey === date.key ? "selected" : ""
                  }`}
                >
                  <p className="dateNumber">{date.day}</p>
                  <p className="dateMonth">{date.month}</p>
                </div>
              </div>
            ))}
          </div>

          {selectedDateKey !== null && hours.length > 0 && (
            <div ref={hoursSectionRef}>
              <p>Selecciona un horario</p>
              <div className="hours">
                {hours.map((hour, index) => (
                  <Button
                    key={index}
                    type={selectedHourIndex === index ? "fullBlack" : "full"}
                    onClick={() => handleHourClick(index)}
                  >
                    {hour.start} \ {hour.end}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

{deliveryMethod === "domicilio" && selectedDateKey !== null && selectedHourIndex !== null && (
            <div className="inputFields" ref={inputFieldsRef}>
              <Input
                placeholder="Ingresa tu dirección"
                label="Dirección"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />
              <Input
                placeholder="Ingresa el complemento de tu dirección"
                label="Complemento dirección (opcional)"
                value={address2}
                onChange={(e) => setAddress2(e.target.value)}
              />
            </div>
          )}

{selectedDateKey !== null && selectedHourIndex !== null && (
        <div className="inputFields" ref={inputFieldsRef}>
          <div
            className={
              isButtonEnabled
                ? "scheduleVisitButton enabled"
                : "scheduleVisitButton"
            }
            onClick={handleScheduleClick}
          >
            <p>Enviar</p>
            <SendArrow color={isButtonEnabled ? "white" : "#0B0F10"} />
          </div>
        </div>
      )}

      <BackButton onClick={onBack} />
    </div>
  );
}

export default ScheduleVisit;
